import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { ThemePicker } from '../ThemePicker/ThemePicker';
import type { SidebarUser } from './Sidebar';

const MENU_GAP_PX = 8;
const COLLAPSED_MENU_WIDTH = 240;

interface MenuPosition {
  left: number;
  bottom: number;
  width: number;
  maxHeight: number;
}

function computeMenuPosition(anchor: DOMRect, preferredWidth: number): MenuPosition {
  const width = Math.max(anchor.width, preferredWidth);
  const left = Math.min(
    Math.max(anchor.left, MENU_GAP_PX),
    window.innerWidth - width - MENU_GAP_PX,
  );
  const bottom = window.innerHeight - anchor.top + MENU_GAP_PX;
  const maxHeight = Math.max(anchor.top - MENU_GAP_PX, 120);

  return { left, bottom, width, maxHeight };
}

function MenuRow({
  icon,
  label,
  onClick,
  tone,
}: {
  icon: IconKind;
  label: string;
  onClick?: () => void;
  tone?: 'danger';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full cursor-pointer items-center gap-2.5 border-0 bg-transparent px-3 py-2.5 text-left font-[inherit] transition-colors',
        tone === 'danger'
          ? 'text-[var(--terracotta)] hover:bg-[var(--terracotta-tint)]'
          : 'text-[var(--ink-1)] hover:bg-[var(--cream-1)]',
      )}
    >
      <Icon kind={icon} size={15} stroke={1.8} color="currentColor" />
      <span className="text-[13px] font-medium">{label}</span>
    </button>
  );
}

export interface SidebarUserMenuProps {
  user: SidebarUser;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collapsed?: boolean;
}

export function SidebarUserMenu({ user, open, onOpenChange, collapsed }: SidebarUserMenuProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setMenuPosition(computeMenuPosition(rect, collapsed ? COLLAPSED_MENU_WIDTH : rect.width));
  }, [collapsed]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPosition(null);
      return;
    }
    updateMenuPosition();
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return;

    const onReposition = () => {
      updateMenuPosition();
    };

    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);

    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      onOpenChange(false);
    };
    // Use click (not mousedown) so theme picker buttons receive their click first.
    document.addEventListener('click', onDoc);
    return () => {
      document.removeEventListener('click', onDoc);
    };
  }, [open, onOpenChange]);

  const portaledMenu =
    open && menuPosition
      ? createPortal(
          <div
            ref={menuRef}
            role="menu"
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            style={{
              position: 'fixed',
              left: menuPosition.left,
              bottom: menuPosition.bottom,
              width: menuPosition.width,
              maxHeight: menuPosition.maxHeight,
              zIndex: 'var(--z-dropdown)',
            }}
            className="overflow-y-auto overflow-x-hidden rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-0)] shadow-[var(--sh-3)]"
          >
            <div className="border-b border-[var(--cream-2)] py-1">
              <MenuRow icon="team" label="Profile" {...(user.onProfile ? { onClick: user.onProfile } : {})} />
              <MenuRow icon="cog" label="Settings" {...(user.onSettings ? { onClick: user.onSettings } : {})} />
            </div>
            {user.showThemePicker !== false && (
              <div className="border-b border-[var(--cream-2)] py-2">
                <div className={cn('px-3 pb-1.5 font-[var(--font-mono)] text-[9.5px] font-semibold uppercase tracking-[0.12em] text-[var(--ink-3)]')}>
                  Theme
                </div>
                <ThemePicker variant="surface" />
              </div>
            )}
            <div className="py-1">
              <MenuRow
                icon="logout"
                label="Log out"
                tone="danger"
                onClick={() => {
                  onOpenChange(false);
                  user.onLogout?.();
                }}
              />
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          onOpenChange(!open);
        }}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-[var(--r-md)] border-0 bg-transparent px-2 py-1.5 text-left font-[inherit] transition-colors hover:bg-[var(--nav-hover-bg)]',
          collapsed && 'justify-center px-1',
        )}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[var(--gold)] to-[var(--ai-3)] text-[11px] font-bold text-white">
          {user.avatar ? (
            <img src={user.avatar} alt="" className="h-full w-full rounded-full object-cover" />
          ) : (
            user.initials ?? user.name.slice(0, 2).toUpperCase()
          )}
        </span>
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1">
              <span className="block text-[12.5px] font-semibold text-[var(--sidebar-ink-strong)]">{user.name}</span>
              {user.role && <span className="block text-[11px] text-[var(--sidebar-muted)]">{user.role}</span>}
            </span>
            <Icon kind="down" size={13} color="var(--sidebar-icon)" />
          </>
        )}
      </button>
      {portaledMenu}
    </div>
  );
}
